/*
    Per-ad-group minimum daily spend, ported from the prototype's `floorFails` /
    `underGroups`. Below its floor a network will not serve at all, so this blocks Save
    (MPN-1805) rather than warning — the Networks table still shows the same failures as a
    row-level diagnostic so Ops can see which rows to fix.
*/

import {
  D,
  Segment,
  Flight,
  ScheduledRange,
  fmtRange,
  payDays,
  effectiveMix,
} from "./segmentEngine"

/**
 * Published network minimums. Only set where the figure is known; a network without one
 * never flags. The floor is per ad group, not per network total — a network at $8/day split
 * evenly across two groups is two groups at $4/day, and both fail a $5 floor while the
 * network total looks healthy.
 */
export const NETWORK_MIN_DAILY: Record<string, number> = {
  meta: 1,
  pinterest: 1,
  google: 5,
  snapchat: 5,
}

export const minDailyFor = (networkKey: string): number =>
  NETWORK_MIN_DAILY[networkKey.toLowerCase()] ?? 0

export type FloorAdGroup = {
  key: string
  name: string
  /** This group's fraction of its network's spend. */
  share: number
  on: boolean
}

export type FloorNetwork = {
  key: string
  name: string
  isConfigured: boolean
  /** The network's own status, before any per-segment override or pending toggle. */
  active: boolean
  /** This network's serving ranges. A network that narrows the campaign's schedule has
   *  fewer days to spend over, so its daily rate is higher and its floor bites sooner. */
  scheduledRanges: readonly ScheduledRange[]
  adGroups: FloorAdGroup[]
}

export type FloorFailure = {
  network: string
  /** Network name alone when it has a single group, "Network · Group" when it splits. */
  who: string
  segment: string
  min: number
  rate: number
}

export type FloorContext = {
  networks: FloorNetwork[]
  defaultMix: Record<string, number>
  flight: Flight
  fundedDays: number
  today: string
  /** Un-saved toggles, keyed `${segmentId}` -> `${networkKey}` / `agKey(network, adGroup)`. */
  pendingNet?: Record<string, Record<string, boolean>>
  pendingAg?: Record<string, Record<string, boolean>>
}

const has = (toggles: Record<string, boolean> | undefined, key: string): boolean =>
  !!toggles && Object.prototype.hasOwnProperty.call(toggles, key)

/**
 * The key an ad group's Active toggle is recorded under, for one network.
 *
 * Its id, not its name. The name shown in the table is the audience's where it has one, and
 * on the production dump one campaign (launch 141) has two ad groups both displaying "Penn
 * Foster Trades Sept26" -- keyed by name, pausing one paused both. That was survivable while
 * the toggle only lived in the draft; now that a pause is written as a policy on a specific
 * ad group (MPN-1893), it would write the wrong one.
 */
export const agKey = (network: Pick<FloorNetwork, "key">, adGroup: Pick<FloorAdGroup, "key">) =>
  network.key + "|" + adGroup.key

export const networkOnIn = (segment: Segment, network: FloorNetwork): boolean =>
  has(segment.netOn, network.key) ? (segment.netOn as Record<string, boolean>)[network.key] : network.active

export const adGroupOnIn = (segment: Segment, network: FloorNetwork, adGroup: FloorAdGroup): boolean => {
  const key = agKey(network, adGroup)
  return has(segment.agOn, key) ? (segment.agOn as Record<string, boolean>)[key] : adGroup.on
}

/** Dollars per funded day reaching one ad group on one network, within one segment. */
export const adGroupDailyRate = (
  segment: Segment,
  network: FloorNetwork,
  adGroup: FloorAdGroup,
  context: Pick<FloorContext, "defaultMix" | "fundedDays">,
): number => {
  const mix = effectiveMix(segment, context.defaultMix)
  const payableDays = payDays(segment, network.scheduledRanges, context.fundedDays)
  if (payableDays <= 0) return 0
  return (
    ((segment.amount * (mix[network.key] || 0)) / 100 / payableDays) * (adGroup.share || 0)
  )
}

/**
 * Every ad group under its floor, across the current and upcoming segments of one pattern.
 *
 * Elapsed segments are skipped: they have already spent, and raising them would rewrite
 * history. Pending pauses are applied first, so pausing the offending network or ad group is
 * one of the ways to clear the block.
 */
export const floorFailures = (segments: Segment[], context: FloorContext): FloorFailure[] => {
  const out: FloorFailure[] = []

  segments.forEach((segment) => {
    if (D(segment.end) < D(context.today)) return

    const pendingNet = context.pendingNet?.[segment.id] || {}
    const pendingAg = context.pendingAg?.[segment.id] || {}

    context.networks.forEach((network) => {
      const min = minDailyFor(network.key)
      if (!network.isConfigured || min <= 0) return

      const netOn = has(pendingNet, network.key) ? pendingNet[network.key] : networkOnIn(segment, network)
      if (!netOn) return

      network.adGroups.forEach((adGroup) => {
        const key = agKey(network, adGroup)
        const groupOn = has(pendingAg, key)
          ? pendingAg[key] : adGroupOnIn(segment, network, adGroup)
        if (!groupOn) return

        const rate = adGroupDailyRate(segment, network, adGroup, context)
        if (rate >= min) return

        out.push({
          network: network.name,
          who: network.adGroups.length > 1 && adGroup.name ? network.name + " · " + adGroup.name : network.name,
          segment: fmtRange(segment.start, segment.end),
          min,
          rate,
        })
      })
    })
  })

  return out
}

export const floorMessage = (fails: FloorFailure[], money: (amount: number) => string): string | null => {
  if (!fails.length) return null
  if (fails.length === 1) {
    return `${fails[0].network} is below its ${money(fails[0].min)}/day minimum (${fails[0].segment})`
  }
  const names = Array.from(new Set(fails.map((f) => f.network))).join(", ")
  return `${fails.length} networks are below their daily minimum (${names})`
}
